// One WebSocket per app connection to /v1/listen, shared by every listener. Reconnects with
// backoff and re-subscribes with what each listener holds (`have`), so the server answers with
// deltas; re-sends the token when it changes (refresh) or when the server says it expired.
import type { DocOut, Query } from "@estra/schema";

export type ListenTarget = { doc: string } | { query: Query };
export interface Snap {
  reset?: boolean;
  order?: string[];
  docs: DocOut[];
  removed?: string[];
  /** Live aggregate listeners: the new count/sum/avg values. */
  aggregate?: Record<string, number | null>;
  /** Offline cache: whether this result came from the server and includes local writes. */
  meta?: { fromCache: boolean; hasPendingWrites: boolean };
}
export interface ListenHandlers {
  next(s: Snap): void;
  error(e: { code: string; message: string }): void;
  /**
   * Resume: the docs this listener already holds (path → version), sent when it (re)subscribes so
   * the server answers with a delta instead of a reset. Undefined = nothing held.
   */
  resume?(): Record<string, number> | undefined;
}
export type Listen = (target: ListenTarget, h: ListenHandlers) => () => void;

export interface RealtimeOptions {
  url: string;
  getToken?: () => Promise<string | null> | string | null;
  WebSocket?: typeof WebSocket;
  /** Re-send the token this often so long-lived connections outlive token expiry (default 4 min). */
  tokenCheckMs?: number;
}

export function realtimeConnection(o: RealtimeOptions): { listen: Listen; close(): void; refreshAuth(): void } {
  const WS = o.WebSocket ?? globalThis.WebSocket;
  const wsUrl = o.url.replace(/^http/, "ws").replace(/\/$/, "") + "/v1/listen";
  const subs = new Map<string, { target: ListenTarget; h: ListenHandlers }>();
  let ws: WebSocket | null = null;
  let ready = false;
  let lastToken: string | null | undefined;
  let backoff = 500;
  let seq = 0;
  let timer: ReturnType<typeof setInterval> | null = null;
  let closed = false;

  const send = (m: unknown) => {
    if (ws && ready) ws.send(JSON.stringify(m));
  };
  const subMsg = (id: string, s: { target: ListenTarget; h: ListenHandlers }) => {
    const have = s.h.resume?.();
    return { t: "sub", id, ...s.target, ...(have ? { have } : {}) };
  };
  const sendAuth = async (force = false) => {
    const t = o.getToken ? await o.getToken() : null;
    if (!force && t === lastToken) return;
    lastToken = t;
    ws?.send(JSON.stringify({ t: "auth", token: t }));
  };

  function open() {
    if (ws || closed || !subs.size) return;
    if (!WS) throw new Error("no WebSocket available: pass RealtimeOptions.WebSocket");
    const sock = new WS(wsUrl);
    ws = sock;
    ready = false;
    sock.addEventListener("open", () => {
      backoff = 500;
      void sendAuth(true);
    });
    sock.addEventListener("message", (e) => {
      let m: { t: string; id?: string; code?: string; message?: string } & Snap;
      try {
        m = JSON.parse(String((e as MessageEvent).data));
      } catch {
        return;
      }
      if (m.t === "ready") {
        if (!ready) {
          ready = true;
          for (const [id, s] of subs) send(subMsg(id, s));
        }
      } else if (m.t === "snap" && m.id) subs.get(m.id)?.h.next(m);
      else if (m.t === "error") {
        if (m.id) {
          const s = subs.get(m.id);
          subs.delete(m.id);
          s?.h.error({ code: m.code ?? "internal", message: m.message ?? "listener failed" });
        } else if (m.code === "unauthenticated" && ready) void sendAuth(true);
      }
    });
    sock.addEventListener("close", () => {
      ws = null;
      ready = false;
      if (closed || !subs.size) return;
      setTimeout(open, backoff);
      backoff = Math.min(backoff * 2, 30_000);
    });
    timer ??= setInterval(() => {
      if (ready) void sendAuth();
    }, o.tokenCheckMs ?? 240_000);
  }

  return {
    /** Send the current token now (call after sign-in / sign-out so open listeners switch user). */
    refreshAuth() {
      if (ready) void sendAuth();
    },
    listen(target, h) {
      const id = `l${++seq}`;
      subs.set(id, { target, h });
      if (ws && ready) send(subMsg(id, { target, h }));
      else open();
      return () => {
        if (!subs.delete(id)) return;
        send({ t: "unsub", id });
        if (!subs.size) {
          ws?.close(1000, "no listeners");
          if (timer) clearInterval(timer);
          timer = null;
        }
      };
    },
    close() {
      closed = true;
      subs.clear();
      if (timer) clearInterval(timer);
      ws?.close(1000, "closed");
    },
  };
}
